/* Message lists: a searchable card of rows. A row shows the list's colour,
   counts and the campaigns using it; opening it edits the list in place —
   name, media vaults, the attach-media chance, new messages (typed or
   generated by topic) and the existing messages. */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MESSAGE_TOPIC_OPTIONS, api, type MessageTopic, type OfVault } from '../../../api/client';
import { Btn, Icon, RefreshBtn, Spin } from '../../ChatAssistant/compact/kit';
import { font as f } from '../../ChatAssistant/compact/style';
import { C } from '../../ChatAssistant/compact/tokens';
import { campaignsUsingList, deleteListConfirmText, deleteListOutcomeText } from '../listDeleteImpact';
import { massApi, type ListMsg, type MsgList } from './massApi';
import type { MassData } from './MassMessagesPage';
import { LIGHT_COLORS, listColor, type Kind } from './model';
import { BlockHead, CountChip, IconBtn, Menu, Panel, PanelRow, STRIPES, SectionHead } from './parts';

const PREVIEW = 8;
const GENERATE_COUNT = 10;

export function ListsSection({ kind, accountId, data, onCopy }: { kind: Kind; accountId: string; data: MassData; onCopy: () => void }) {
  const { t } = useTranslation('massCompact');
  const { t: tm } = useTranslation('massMessages');
  const a = useMemo(() => massApi(kind, accountId), [kind, accountId]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const [vaults, setVaults] = useState<OfVault[] | null>(null);
  const [creating, setCreating] = useState(false);

  // Vaults are only needed once a list is opened.
  useEffect(() => {
    if (open === null || vaults) return;
    api.getVaultsForAccount(accountId).then(setVaults).catch((e) => { console.error('Failed to load vaults:', e); setVaults([]); });
  }, [open, vaults, accountId]);
  const refreshVaults = async () => { setVaults(await api.syncAccountVaults(accountId)); };

  const total = data.lists.reduce((s, l) => s + (l.message_count ?? 0), 0);
  const shown = data.lists.filter((l) => l.name.toLowerCase().includes(q.trim().toLowerCase()));

  const create = async () => {
    setCreating(true);
    try {
      const l = await a.createList({ name: t('lists.newName'), content_probability: 0.5 });
      data.setLists((ls) => [...ls, l]);
      setQ('');
      setOpen(l.id);
    } catch (e) {
      console.error('Failed to create list:', e);
      alert(t('lists.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (l: MsgList) => {
    const refs = data.camps.map((c) => ({ id: c.id!, name: c.name, is_active: c.active, lists_distribution: Object.fromEntries(c.dist.map((d) => [d.id, d.w])) }));
    if (!confirm(deleteListConfirmText(tm, campaignsUsingList(refs, l.id), t('lists.deleteConfirm', { name: l.name })))) return;
    try {
      const res = await a.deleteList(l.id);
      if (open === l.id) setOpen(null);
      data.setLists((ls) => ls.filter((x) => x.id !== l.id));
      const notice = deleteListOutcomeText(tm, res);
      if (notice) alert(notice);
      // The server dropped the list from campaign weights (and may have paused some).
      void data.reload();
    } catch (e) {
      console.error('Failed to delete list:', e);
      alert(t('lists.deleteFailed'));
    }
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <SectionHead title={t('lists.title')} sub={t('lists.summary', { count: data.lists.length, messages: t('lists.messages', { count: total }) })}>
        <label style={{ height: 28, width: 200, padding: '0 9px', display: 'flex', alignItems: 'center', gap: 7, boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 7 }}>
          <Icon name="search" size={13} color={C.muted} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('lists.search')} style={{ flex: 1, minWidth: 0, border: 0, outline: 0, background: 'transparent', color: C.text, font: f(400, 12) }} />
        </label>
        <Btn icon="copy" onClick={onCopy} style={{ background: C.input, borderColor: C.border }}>{t('copyFromAccount')}</Btn>
        <Btn kind="tint" icon="plus" disabled={creating} onClick={() => { void create(); }} style={{ color: 'var(--om-accent-4)', fontWeight: 600 }}>{t('lists.new')}</Btn>
      </SectionHead>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }}>
        {shown.map((l, vi) => (
          <ListRow
            key={l.id}
            kind={kind}
            accountId={accountId}
            list={l}
            color={listColor(data.lists.indexOf(l))}
            first={vi === 0}
            open={open === l.id}
            onToggle={() => setOpen((o) => (o === l.id ? null : l.id))}
            usedBy={data.camps.filter((c) => !(kind === 'mass' && c.source === 'auto') && c.dist.some((d) => d.id === String(l.id)))}
            vaults={vaults}
            onRefreshVaults={refreshVaults}
            onPatch={(p) => data.setLists((ls) => ls.map((x) => (x.id === l.id ? { ...x, ...p } : x)))}
            onDelete={() => { void remove(l); }}
          />
        ))}
        {!shown.length && <div style={{ padding: '22px 14px', font: f(400, 12.5), color: C.sub, textAlign: 'center' }}>{data.lists.length ? t('lists.noMatch') : t('lists.none')}</div>}
      </div>
    </section>
  );
}

function ListRow({ kind, accountId, list, color, first, open, onToggle, usedBy, vaults, onRefreshVaults, onPatch, onDelete }: {
  kind: Kind; accountId: string; list: MsgList; color: string; first: boolean; open: boolean; onToggle: () => void;
  usedBy: { id: number | null; name: string; active: boolean }[]; vaults: OfVault[] | null; onRefreshVaults: () => Promise<void>;
  onPatch: (p: Partial<MsgList>) => void; onDelete: () => void;
}) {
  const { t } = useTranslation('massCompact');
  const a = useMemo(() => massApi(kind, accountId), [kind, accountId]);
  const [msgs, setMsgs] = useState<ListMsg[] | null>(null);
  const [all, setAll] = useState(false);
  const [msgQuery, setMsgQuery] = useState('');
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState(list.name);
  const [prob, setProb] = useState(Math.round((list.content_probability ?? 0.5) * 100));
  const probTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setName(list.name); }, [list.name]);
  useEffect(() => { setProb(Math.round((list.content_probability ?? 0.5) * 100)); }, [list.content_probability]);
  useEffect(() => () => { if (probTimer.current) clearTimeout(probTimer.current); }, []);

  const loadMsgs = async () => {
    try {
      const m = await a.getMessages(list.id);
      setMsgs(m);
      if ((list.message_count ?? 0) !== m.length) onPatch({ message_count: m.length });
    } catch (e) {
      console.error('Failed to load messages:', e);
      setMsgs([]);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && msgs === null) void loadMsgs(); }, [open]);

  const save = async (p: Partial<MsgList>) => {
    onPatch(p);
    try { await a.updateList(list.id, p); }
    catch (e) { console.error('Failed to update list:', e); alert(t('lists.updateFailed')); }
  };
  const commitName = () => {
    const v = name.trim();
    if (!v) { setName(list.name); return; }
    if (v !== list.name) void save({ name: v });
  };
  const changeProb = (v: number) => {
    setProb(v);
    if (probTimer.current) clearTimeout(probTimer.current);
    probTimer.current = setTimeout(() => { void save({ content_probability: v / 100 }); }, 400);
  };

  const linked = list.list_of_media_vaults ?? [];
  const free = (vaults ?? []).filter((v) => !linked.includes(v.vault_name));
  const parts = draft.split(';').map((s) => s.trim()).filter(Boolean).length;
  const count = msgs?.length ?? list.message_count ?? 0;
  const sent = msgs ? msgs.reduce((s, m) => s + (m.times_sent ?? 0), 0) : null;
  const normalizedMsgQuery = msgQuery.trim().toLocaleLowerCase();
  const matchingMsgs = msgs?.filter((m) => m.message_text.toLocaleLowerCase().includes(normalizedMsgQuery)) ?? [];
  const visible = normalizedMsgQuery ? matchingMsgs : (all ? matchingMsgs : matchingMsgs.slice(0, PREVIEW));

  const add = async () => {
    if (!parts || adding) return;
    setAdding(true);
    try { await a.addMessages(list.id, draft); setDraft(''); await loadMsgs(); }
    catch (e) { console.error('Failed to add messages:', e); alert(t('lists.addFailed')); }
    finally { setAdding(false); }
  };
  const generate = async (topic: MessageTopic) => {
    setGenerating(true);
    try {
      const { messages } = await a.randomTexts(topic, GENERATE_COUNT);
      if (!messages.length) { alert(t('lists.noPublic')); return; }
      setDraft((d) => (d.trim() ? `${d.trimEnd()}; ${messages.join('; ')}` : messages.join('; ')));
    } catch (e) {
      console.error('Failed to generate messages:', e);
      alert(t('lists.generateFailed'));
    } finally {
      setGenerating(false);
    }
  };
  const delMsg = async (m: ListMsg) => {
    setMsgs((ms) => ms && ms.filter((x) => x.id !== m.id));
    onPatch({ message_count: Math.max(0, count - 1) });
    try { await a.removeMessage(list.id, m.id); }
    catch (e) { console.error('Failed to delete message:', e); alert(t('lists.deleteMsgFailed')); void loadMsgs(); }
  };
  const copyAll = () => {
    if (!msgs?.length) return;
    navigator.clipboard?.writeText(msgs.map((m) => m.message_text).join('\n')).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={{ borderTop: first ? 0 : `1px solid ${C.divider}` }}>
      <div role="button" tabIndex={0} onClick={onToggle} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }} className="cpt-hover-row" style={{ minHeight: 50, padding: '8px 10px 8px 12px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', boxSizing: 'border-box' }}>
        <Icon name="chevron-right" size={14} color={C.muted} style={{ flex: 'none', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
        <span style={{ width: 28, height: 28, flex: 'none', borderRadius: 7, background: color, color: LIGHT_COLORS.has(color) ? C.card : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>
          {list.emoji || <Icon name="folder" size={14} />}
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
          <span title={list.name} style={{ font: f(600, 13), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{list.name}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12, font: f(400, 11.5), color: C.sub, whiteSpace: 'nowrap' }}>
            <Meta icon="message-square">{t('lists.messages', { count })}</Meta>
            <Meta icon="image">{t('lists.vaults', { count: linked.length })}</Meta>
            {sent !== null && <Meta icon="send">{t('lists.sent', { count: sent, n: sent.toLocaleString() })}</Meta>}
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 4 }}>
          {usedBy.map((c) => (
            <span key={c.id} style={{ height: 20, padding: '0 7px', display: 'flex', alignItems: 'center', gap: 4, borderRadius: 99, background: c.active ? C.tint16 : C.borderLo, color: c.active ? C.purplePale : C.sub, font: f(500, 11), whiteSpace: 'nowrap' }}>
              <Icon name="target" size={11} />{c.name}
            </span>
          ))}
          {!usedBy.length && <span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap' }}>{t('lists.unused')}</span>}
        </div>
        <IconBtn icon="trash-2" title={t('lists.delete')} onClick={onDelete} color={C.muted} />
      </div>

      {open && (
        <div style={{ padding: '4px 14px 16px 52px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Panel>
            <PanelRow label={t('lists.name')}>
              <input value={name} onChange={(e) => setName(e.target.value)} onBlur={commitName} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} style={{ width: 260, maxWidth: '100%', height: 28, padding: '0 9px', boxSizing: 'border-box', background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, font: f(500, 12), outline: 'none' }} />
            </PanelRow>
            <PanelRow label={t('lists.vaultsLabel')} help={t('lists.vaultsHelp')}>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: 6 }}>
                {linked.map((v) => (
                  <span key={v} style={{ height: 26, padding: '0 4px 0 6px', display: 'flex', alignItems: 'center', gap: 6, boxSizing: 'border-box', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 7, font: f(500, 12), color: C.text, whiteSpace: 'nowrap' }}>
                    <span style={{ width: 16, height: 16, borderRadius: 4, background: STRIPES }} />{v}
                    <IconBtn icon="x" title={t('lists.removeVault')} size={18} iconSize={11} color={C.muted} onClick={() => { void save({ list_of_media_vaults: linked.filter((x) => x !== v) }); }} />
                  </span>
                ))}
                <Menu
                  look="dashed"
                  height={26}
                  align="right"
                  label={vaults === null ? '…' : free.length ? t('lists.addVault') : t('lists.allVaults')}
                  disabled={vaults === null || !free.length}
                  options={free.map((v) => ({ id: v.vault_name, label: v.vault_name }))}
                  onPick={(id) => { void save({ list_of_media_vaults: [...linked, id] }); }}
                />
                <RefreshBtn onRefresh={onRefreshVaults} busy={vaults === null} title={t('lists.refreshVaults')} />
              </div>
            </PanelRow>
            <PanelRow label={t('lists.attach')} help={t('lists.attachHelp')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="range" className="cpt-range" min={0} max={100} step={5} value={prob} onChange={(e) => changeProb(Number(e.target.value))} style={{ width: 200, background: `linear-gradient(90deg,${C.purple} ${prob}%,${C.border} ${prob}%)` }} />
                <span style={{ width: 38, textAlign: 'right', font: f(600, 12.5), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{prob}%</span>
              </div>
            </PanelRow>
          </Panel>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <BlockHead title={t('lists.addTitle')} hint={t('lists.addHint')} />
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} disabled={adding} placeholder={t('lists.addPh')} style={{ height: 64, resize: 'vertical', padding: '8px 10px', boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, font: f(400, 12.5, 1.5), outline: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button type="button" onClick={() => { void add(); }} disabled={!parts || adding} style={{ height: 28, padding: '0 12px', border: 0, borderRadius: 7, background: parts ? C.purple : C.borderLo, color: parts ? '#fff' : C.muted, font: f(600, 12), cursor: parts && !adding ? 'pointer' : 'default', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                {adding && <Spin size={12} />}{parts ? t('lists.addN', { count: parts }) : t('lists.addTitle')}
              </button>
              <TopicMenu busy={generating} onPick={(topic) => { void generate(topic); }} />
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <BlockHead
              title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{t('lists.msgsTitle')}<CountChip>{normalizedMsgQuery ? `${matchingMsgs.length}/${count}` : count}</CountChip></span>}
              right={(
                <div className="cpt-message-tools" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
                  <div className="cpt-message-search" style={{ height: 28, width: 220, padding: '0 5px 0 9px', display: 'flex', alignItems: 'center', gap: 7, boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 7 }}>
                    <Icon name="search" size={13} color={C.muted} />
                    <input
                      type="search"
                      name={`message-search-${list.id}`}
                      value={msgQuery}
                      onChange={(e) => setMsgQuery(e.target.value)}
                      placeholder={t('lists.searchMessages')}
                      aria-label={t('lists.searchMessages')}
                      autoComplete="off"
                      spellCheck={false}
                      style={{ flex: 1, minWidth: 0, border: 0, outline: 0, background: 'transparent', color: C.text, font: f(400, 12) }}
                    />
                    {msgQuery && <IconBtn icon="x" title={t('lists.clearSearch')} size={20} iconSize={11} color={C.muted} onClick={() => setMsgQuery('')} />}
                  </div>
                  <button type="button" onClick={copyAll} disabled={!msgs?.length} className="cpt-hover-icon" style={{ height: 26, padding: '0 8px', border: 0, borderRadius: 6, background: 'transparent', color: C.sub, font: f(500, 12), cursor: msgs?.length ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                    <Icon name="copy" size={12} />{copied ? t('lists.copied') : t('lists.copyAll')}
                  </button>
                </div>
              )}
            />
            <div style={{ background: C.input, border: `1px solid ${C.borderLo}`, borderRadius: 8 }}>
              {msgs === null && <div style={{ padding: 16, display: 'flex', justifyContent: 'center', color: C.muted }}><Spin size={14} /></div>}
              {visible.map((m, j) => (
                <div key={m.id} className="cpt-hover-row" style={{ display: 'flex', alignItems: 'center', gap: 12, height: 36, padding: '0 6px 0 12px', borderTop: j ? `1px solid ${C.divider}` : 0 }}>
                  <span title={m.message_text} style={{ flex: 1, minWidth: 0, font: f(400, 12.5), color: 'var(--om-text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.message_text}</span>
                  <span style={{ flex: 'none', width: 100, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5, font: f(500, 11.5), color: m.times_sent ? C.sub : C.purplePale, whiteSpace: 'nowrap' }}>
                    <Icon name={m.times_sent ? 'send' : 'circle'} size={11} />{m.times_sent ? t('lists.sentTimes', { count: m.times_sent }) : t('lists.notSent')}
                  </span>
                  <IconBtn icon="trash-2" title={t('lists.deleteMsg')} size={26} iconSize={12} color={C.muted} onClick={() => { void delMsg(m); }} />
                </div>
              ))}
              {msgs !== null && !msgs.length && <div style={{ padding: 16, font: f(400, 12), color: C.muted, textAlign: 'center' }}>{t('lists.noMsgs')}</div>}
              {msgs !== null && msgs.length > 0 && normalizedMsgQuery && !matchingMsgs.length && <div style={{ padding: 16, font: f(400, 12), color: C.muted, textAlign: 'center' }}>{t('lists.noMessageMatch')}</div>}
              {msgs !== null && !normalizedMsgQuery && msgs.length > PREVIEW && (
                <button type="button" onClick={() => setAll((x) => !x)} className="cpt-hover-row" style={{ width: '100%', height: 32, border: 0, borderTop: `1px solid ${C.divider}`, borderRadius: '0 0 8px 8px', background: 'transparent', color: C.purpleLt, font: f(500, 12), cursor: 'pointer' }}>
                  {all ? t('lists.showLess') : t('lists.showAll', { count: msgs.length })}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Meta({ icon, children }: { icon: string; children: ReactNode }) {
  return <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Icon name={icon} size={12} color={C.muted} />{children}</span>;
}

/** "Generate by topic": pick a topic and its public texts are appended to the draft. */
function TopicMenu({ busy, onPick }: { busy: boolean; onPick: (t: MessageTopic) => void }) {
  const { t } = useTranslation('massCompact');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', down);
    return () => document.removeEventListener('mousedown', down);
  }, [open]);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" disabled={busy} onClick={() => setOpen((o) => !o)} className="cpt-hover" title={t('lists.generateTip', { count: GENERATE_COUNT })} style={{ height: 28, padding: '0 10px', border: 0, borderRadius: 7, background: 'transparent', color: C.purpleLt, font: f(500, 12), cursor: busy ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
        {busy ? <Spin size={13} /> : <Icon name="zap" size={13} />}{t('lists.generate')}
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 32, left: 0, zIndex: 41, width: 200, padding: 4, boxSizing: 'border-box', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 8, boxShadow: '0 12px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ padding: '5px 8px 4px', font: f(600, 10), letterSpacing: '.08em', textTransform: 'uppercase', color: C.muted }}>{t('lists.topic')}</span>
          {MESSAGE_TOPIC_OPTIONS.map((topic) => (
            <button key={topic} type="button" className="cpt-hover-row" onClick={() => { setOpen(false); onPick(topic); }} style={{ minHeight: 30, padding: '5px 8px', border: 0, borderRadius: 5, background: 'transparent', color: C.text, font: f(500, 12), cursor: 'pointer', textAlign: 'left' }}>
              {t(`topic.${topic}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
